import axios from "axios";
import { userIdentity } from "./identity";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// `withCredentials: true` is REQUIRED so the browser ships the
// rr_admin_token / rr_agency_token httpOnly cookies on every request.
// Combined with the explicit-origin CORS config server-side, this
// gives us hardened cookie-based auth without breaking same-origin
// preview deployments.
//
// Default Content-Type is intentionally NOT set globally. axios picks
// the right one per request body: `application/json` for plain
// objects, the auto-generated `multipart/form-data; boundary=…` for
// `FormData`, etc. Forcing a global JSON header used to silently
// strip the multipart boundary on uploads ("Échec de l'envoi du média").
export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Read a non-httpOnly cookie value from document.cookie. We use this
// to grab the CSRF tokens (rr_admin_csrf / rr_agency_csrf) so we can
// double-submit them in the X-CSRF-Token header.
const readCookie = (name) => {
  if (typeof document === "undefined") return "";
  const prefix = `${name}=`;
  for (const part of document.cookie.split(";")) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length));
    }
  }
  return "";
};

// Attach the citizen device token (see citizen_auth.py) on every
// request. Harmless for admin/agency-only endpoints — they use
// separate cookie-based auth and simply ignore this header.
api.interceptors.request.use((config) => {
  const token = userIdentity.getToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers["X-Citizen-Token"] = token;
  }
  return config;
});

// CSRF double-submit interceptor: for every state-changing request,
// copy whichever CSRF cookie is currently set (admin OR agency — only
// one is active at a time per browser context) into the X-CSRF-Token
// header. Safe methods (GET/HEAD/OPTIONS) are skipped.
//
// IMPORTANT — pick the right cookie based on the request path. When
// a single browser holds BOTH an admin session (rr_admin_csrf) AND an
// agency session (rr_agency_csrf) — e.g. an admin opens the agency
// dashboard in another tab — we MUST send the matching token to the
// matching endpoint. Sending admin CSRF to /agency/* (or vice-versa)
// makes the backend reject with "Jeton CSRF invalide".
const STATE_CHANGING = new Set(["post", "put", "patch", "delete"]);
api.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  if (!STATE_CHANGING.has(method)) return config;
  // Path may be relative ("/admin/foo") or absolute — strip the base.
  const url = String(config.url || "");
  const isAgencyEndpoint = /(^|\/)agency(\/|$)/.test(url);
  const isAdminEndpoint = /(^|\/)admin(\/|$)/.test(url) && !isAgencyEndpoint;
  let csrf;
  if (isAgencyEndpoint) {
    // For /agency/* always prefer the agency token. Fall back to admin
    // only when agency cookie is missing (super-rare; never legitimate).
    csrf = readCookie("rr_agency_csrf") || readCookie("rr_admin_csrf");
  } else if (isAdminEndpoint) {
    csrf = readCookie("rr_admin_csrf") || readCookie("rr_agency_csrf");
  } else {
    // Public routes (uploads, alerts create, push subscribe, etc.) —
    // either token is fine; pick whichever is present.
    csrf = readCookie("rr_admin_csrf") || readCookie("rr_agency_csrf");
  }
  if (csrf) {
    config.headers = config.headers || {};
    config.headers["X-CSRF-Token"] = csrf;
  }
  return config;
});

// Auth-401 interceptor: when the server rejects a request because the
// httpOnly session cookie has expired or was cleared, drop the local
// "is logged in" flag so the AuthGate redirects to the login screen
// instead of leaving the dashboard in a half-broken state. Lazy import
// of identity.js avoids the circular import with api.js.
api.interceptors.response.use(
  (r) => r,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    if (status === 401 && !url.endsWith("/admin/login") && !url.endsWith("/agency/login")) {
      // Dynamic import sidesteps the identity↔api circular dependency.
      import("./identity").then(({ adminAuth, agencyAuth }) => {
        if (url.startsWith("/admin/") || url.startsWith("/admin")) adminAuth.clear();
        if (url.startsWith("/agency/") || url.startsWith("/agency")) agencyAuth.clear();
      }).catch(() => { /* noop */ });
    }
    return Promise.reject(error);
  },
);

export async function listContacts() {
  const { data } = await api.get("/contacts");
  return data;
}
export async function createContact(payload) {
  const { data } = await api.post("/contacts", payload);
  return data;
}
export async function updateContact(id, payload) {
  const { data } = await api.patch(`/contacts/${id}`, payload);
  return data;
}
export async function deleteContact(id) {
  const { data } = await api.delete(`/contacts/${id}`);
  return data;
}

// Iter74 — Emergency medical profile CRUD. Ownership is resolved
// server-side from the X-Citizen-Token device token (see
// citizen_auth.py) — no phone is sent on the wire anymore (Sep 2026
// security-audit code review: the old reporter_phone query param was
// dead weight leaking phone numbers into access logs/URLs).
export async function getMedicalProfile() {
  if (!userIdentity.getToken()) return {};
  const { data } = await api.get("/citizen/medical-profile");
  return data || {};
}
export async function saveMedicalProfile(payload) {
  if (!userIdentity.getToken()) throw new Error("Aucun numéro de téléphone associé");
  const { data } = await api.put("/citizen/medical-profile", payload);
  return data;
}
export async function clearMedicalProfile() {
  if (!userIdentity.getToken()) return { deleted: 0 };
  const { data } = await api.delete("/citizen/medical-profile");
  return data;
}

export async function getSettings() {
  const { data } = await api.get("/settings");
  return data;
}
export async function updateSettings(payload) {
  const { data } = await api.put("/settings", payload);
  return data;
}
export async function createAlert(payload) {
  const phone = userIdentity.getPhone();
  const body = phone ? { ...payload, reporter_phone: phone } : payload;
  const { data } = await api.post("/alerts", body);
  return data;
}
// Iter90 — GPS audit: push a post-SOS location refinement onto an
// EXISTING alert (never creates a new one). Fire-and-forget by the
// caller — a rejected/offline refinement is not fatal, the alert was
// already sent with whatever fix was available at creation time.
export async function updateAlertLocation(alertId, coords) {
  const { data } = await api.patch(`/alerts/${alertId}/location`, {
    latitude: coords.latitude,
    longitude: coords.longitude,
    accuracy: coords.accuracy,
    altitude: coords.altitude,
    altitude_accuracy: coords.altitudeAccuracy,
    heading: coords.heading,
    speed: coords.speed,
    client_ts: coords.timestamp,
  });
  return data;
}
export async function listAlerts() {
  const { data } = await api.get("/alerts");
  return data;
}
export async function getAlert(id) {
  const { data } = await api.get(`/alerts/${id}`);
  return data;
}

// Iter66 (Phase 2) — Client-side image compression before upload.
// Resizes large photos down to a max dimension of 1600 px and re-encodes
// at JPEG quality 0.82, which is still well above the threshold needed
// for emergency triage but typically shrinks a 12 MP phone capture from
// 6–8 MB down to ~400 KB. Audio and video are NEVER touched here (the
// existing 30 MB / 50 MB server-side caps remain authoritative).
//
// Pure-JS / canvas-based to avoid pulling in heavy deps. Returns the
// original File unchanged if:
//   • the input is not an image,
//   • the input is already small (< 800 KB),
//   • the input is an SVG / GIF (animated content survives intact),
//   • compression fails for any reason (we never block uploads).
const _MAX_IMAGE_DIM = 1600;
const _IMAGE_QUALITY = 0.82;
const _SKIP_BELOW_BYTES = 800 * 1024;

export async function compressImageIfPossible(file) {
  try {
    if (!file || typeof file !== "object") return file;
    if (!file.type || !file.type.startsWith("image/")) return file;
    if (file.type === "image/gif" || file.type === "image/svg+xml") return file;
    if (file.size < _SKIP_BELOW_BYTES) return file;
    // Read the file into a bitmap (preferred — fastest path on modern browsers).
    let bitmap;
    if (typeof createImageBitmap === "function") {
      bitmap = await createImageBitmap(file);
    } else {
      // Fallback: <img> element
      const url = URL.createObjectURL(file);
      bitmap = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = url;
      });
      URL.revokeObjectURL(url);
    }
    const w = bitmap.width || bitmap.naturalWidth;
    const h = bitmap.height || bitmap.naturalHeight;
    if (!w || !h) return file;
    const scale = Math.min(1, _MAX_IMAGE_DIM / Math.max(w, h));
    const nw = Math.round(w * scale);
    const nh = Math.round(h * scale);
    const canvas = document.createElement("canvas");
    canvas.width = nw;
    canvas.height = nh;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, nw, nh);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", _IMAGE_QUALITY));
    if (!blob || blob.size >= file.size) return file; // never inflate
    const baseName = (file.name || "photo").replace(/\.[^.]+$/, "");
    return new File([blob], baseName + ".jpg", { type: "image/jpeg" });
  } catch (_e) {
    // Never let compression failures block an emergency alert upload.
    return file;
  }
}

// Media uploads (image / audio / video) — returns {kind, url, name, size, content_type}.
// `url` is a path; pass it via `mediaUrl()` to render or attach to alert/broadcast.
//
// CRITICAL: do NOT set Content-Type manually for FormData. The browser
// MUST set it to `multipart/form-data; boundary=----WebKitFormBoundary…`
// with the auto-generated boundary string. Setting it ourselves to a
// bare "multipart/form-data" strips the boundary and the server fails
// to parse the body (manifests as "Échec de l'envoi du média").
//
// Iter66 — Images are now run through `compressImageIfPossible` first
// (canvas-based JPEG re-encode @ 1600 px / 0.82 quality), which keeps
// uploads inside the existing 25 MB cap and dramatically reduces upload
// time on weak networks. Audio/video pass through unchanged.
export async function uploadMedia(file, onProgress) {
  const optimised = await compressImageIfPossible(file);
  const fd = new FormData();
  fd.append("file", optimised);
  const { data } = await api.post("/uploads", fd, {
    onUploadProgress: (e) => {
      if (!onProgress || !e.total) return;
      onProgress(Math.round((e.loaded / e.total) * 100));
    },
  });
  return data;
}
// Resolve a server-relative upload URL to an absolute URL the browser can fetch.
export const mediaUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${BACKEND_URL}${path}`;
};

// Onboarding
export async function onboardUser(phone) {
  const { data } = await api.post("/users/onboard", { phone });
  if (data?.citizen_token) userIdentity.setToken(data.citizen_token);
  return data;
}

// Iter80 — Phone verification (SMS OTP)
export async function requestPhoneCode(phone) {
  const { data } = await api.post("/auth/phone/request-code", { phone });
  return data;
}

export async function verifyPhoneCode(phone, code) {
  const { data } = await api.post("/auth/phone/verify-code", { phone, code });
  return data;
}

export async function fetchUserVerificationStatus(phone) {
  // Cheap "is my phone still verified" probe used by the OnboardingGate
  // when localStorage has a phone but we want to double-check the
  // backend hasn't reset it. Reuses the existing onboard endpoint
  // which is idempotent — if the user exists, it returns the record
  // (including phone_verified_at); if not, it creates it.
  const { data } = await api.post("/users/onboard", { phone });
  return data;
}

// ====================================================================
// Iter66 (Phase 3-5) — Alert amendments (citizen-side updates).
// ====================================================================

/**
 * Citizen submits an amendment to an alert they previously reported.
 * `reporter_phone` is the ownership gate — must match the alert owner.
 * Returns the citizen-safe projection of the saved amendment.
 */
export async function submitAmendment(alertId, payload) {
  const { data } = await api.post(`/alerts/${alertId}/amendments`, payload);
  return data;
}

/**
 * Citizen lists their own amendments for an alert.
 */
export async function listMyAmendments(alertId, reporterPhone) {
  const { data } = await api.get(`/alerts/${alertId}/amendments`, {
    params: { reporter_phone: reporterPhone },
  });
  return data;
}

/**
 * Admin reads the FULL amendment timeline (includes dispatch context
 * fields the citizen never sees).
 */
export async function adminListAmendments(alertId) {
  const { data } = await api.get(`/admin/alerts/${alertId}/amendments`);
  return data;
}

/**
 * Assigned agency reads the citizen-safe amendment timeline. Backend
 * gates on _check_agency_assigned — unassigned agencies get 403.
 */
export async function agencyListAmendments(alertId) {
  const { data } = await api.get(`/agency/alerts/${alertId}/amendments`);
  return data;
}


// Admin
export async function adminLogin(password) {
  // Backend sets rr_admin_token + rr_admin_csrf cookies on success.
  const { data } = await api.post("/admin/login", { password });
  return data;
}
export async function adminLogout() {
  try { await api.post("/admin/logout", {}); } catch (e) { /* noop */ void e; }
}
export async function adminMe() {
  // Session probe used by AuthGate on mount to re-establish the
  // "logged in" flag after a page refresh.
  const { data } = await api.get("/admin/me");
  return data;
}
export async function adminListUsers(params = {}) {
  const { data } = await api.get("/admin/users", { params });
  return data;
}
export async function adminUpdateUser(phone, payload) {
  const { data } = await api.patch(`/admin/users/${encodeURIComponent(phone)}`, payload);
  return data;
}
export async function adminListAlerts(params = {}) {
  const { data } = await api.get("/admin/alerts", { params });
  return data;
}
export async function adminConfigFilters() {
  const { data } = await api.get("/admin/config/filters");
  return data;
}
export async function adminGetLiveMetrics() {
  const { data } = await api.get("/admin/system/live-metrics");
  return data;
}
export async function adminMarkAlert(id, isFalse) {
  const { data } = await api.patch(`/admin/alerts/${id}`, { is_false: isFalse });
  return data;
}
export async function adminHeatmapPoints(params = {}) {
  const { data } = await api.get("/admin/alerts/heatmap", { params });
  return data;
}
export async function adminHeatmapStats(params = {}) {
  const { data } = await api.get("/admin/alerts/stats", { params });
  return data;
}

// Executive Reports — admin-only aggregated report + CSV / PDF export.
// Cookie auth means we no longer need to embed the token in URL query
// strings; the blob fetch sends the cookie automatically.
export async function adminExecutiveReport(params = {}) {
  const { data } = await api.get("/admin/reports/executive", { params });
  return data;
}
export async function adminExecutiveReportCsvBlob(params = {}) {
  const res = await api.get("/admin/reports/executive.csv", {
    params,
    responseType: "blob",
  });
  return res.data;
}
export async function adminExecutiveReportPdfBlob(params = {}) {
  const res = await api.get("/admin/reports/executive.pdf", {
    params,
    responseType: "blob",
  });
  return res.data;
}

// Admin · Agencies management
export async function adminListAgencies() {
  const { data } = await api.get("/admin/agencies");
  return data;
}
export async function adminCreateAgency(payload) {
  const { data } = await api.post("/admin/agencies", payload);
  return data;
}
export async function adminDeleteAgency(id) {
  const { data } = await api.delete(`/admin/agencies/${id}`);
  return data;
}
export async function adminUpdateAgency(id, patch) {
  const { data } = await api.patch(`/admin/agencies/${id}`, patch);
  return data;
}
export async function adminResetAgencyPassword(id, password) {
  const { data } = await api.post(`/admin/agencies/${id}/reset-password`, { password });
  return data;
}
export async function adminSetAgencyActive(id, active) {
  const path = active ? "activate" : "deactivate";
  const { data } = await api.post(`/admin/agencies/${id}/${path}`);
  return data;
}

// ====================================================================
// Iter55 (Phase 2) — Central-Admin verification & dispatch workflow
// ====================================================================
export async function adminSetReviewStatus(alertId, central_review_status, verification_note) {
  const { data } = await api.post(
    `/admin/alerts/${alertId}/review`,
    { central_review_status, verification_note: verification_note || null },
  );
  return data;
}

export async function adminDispatchAlert(alertId, agency_ids, dispatch_reason) {
  const { data } = await api.post(
    `/admin/alerts/${alertId}/dispatch`,
    { agency_ids, dispatch_reason: dispatch_reason || null },
  );
  return data;
}

export async function adminReassignAlert(alertId, new_agency_ids, reassignment_reason, notify_previous = true) {
  const { data } = await api.post(
    `/admin/alerts/${alertId}/reassign`,
    { new_agency_ids, reassignment_reason, notify_previous },
  );
  return data;
}

export async function agencySetOperationalStatus(alertId, operational_status, note) {
  const { data } = await api.post(
    `/agency/alerts/${alertId}/operational-status`,
    { operational_status, note: note || null },
  );
  return data;
}

// Iter56 (Phase 3) — scoped evidence access for assigned agencies.
export async function agencyGetCaseFile(alertId) {
  const { data } = await api.get(`/agency/alerts/${alertId}/case-file`);
  return data;
}

/**
 * Build the absolute URL for a scoped attachment. The path itself is
 * the source of truth (assignment is verified server-side); we just
 * stitch it onto the configured REACT_APP_BACKEND_URL so <img>/<audio>
 * tags can fetch it with credentials.
 */
export function agencyAttachmentUrl(scoped_url) {
  if (!scoped_url) return "";
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  return base + scoped_url;
}

/**
 * Iter66 (Phase 1) — Fetch a scoped agency attachment as a Blob and
 * wrap it in a blob URL. Required because raw `<img src=…>` /
 * `<audio src=…>` / `<video src=…>` tags do NOT carry the agency
 * authentication cookie when the backend lives on a different origin
 * (e.g. `api.sosmali.com` vs `admin.sosmali.com` in production). The
 * blob URL produced here is local to the browser document and can be
 * cleaned up via `URL.revokeObjectURL`. The actual `fetch` call uses
 * `credentials: "include"` so the same-site/HttpOnly agency cookie
 * ships with the request — the server's existing
 * `_check_agency_assigned` gate stays in charge of authorization.
 *
 * Returns: `{ url, content_type, size }`.
 * Throws: an Error with `.status` mirroring the HTTP status code so
 * the caller can render the right French message ("Accès refusé" on
 * 401/403, "Fichier introuvable" on 404, etc.).
 */
export async function fetchAgencyAttachmentBlob(scoped_url) {
  if (!scoped_url) throw new Error("Pièce jointe manquante");
  const abs = agencyAttachmentUrl(scoped_url);
  const resp = await fetch(abs, { credentials: "include" });
  if (!resp.ok) {
    const err = new Error(
      resp.status === 401 || resp.status === 403 ? "Accès refusé"
        : resp.status === 404 ? "Fichier introuvable"
        : `Erreur ${resp.status}`,
    );
    err.status = resp.status;
    throw err;
  }
  const blob = await resp.blob();
  return {
    url: URL.createObjectURL(blob),
    content_type: resp.headers.get("content-type") || blob.type,
    size: blob.size,
  };
}

// ====================================================================
// Iter59 (Phase 6) — Admin settings (P1)
// ====================================================================
export async function adminGetSettings() {
  const { data } = await api.get("/admin/settings");
  return data;
}
// Iter93 — Planification Phase 1. Real server-side access-control
// round-trip (Depends(require_admin)) called once on mount by
// PlanificationTab, BEFORE rendering the workspace — the tab being
// hidden for agency users is a UX nicety on top of this, never the
// only control.
export async function checkPlanificationAccess() {
  const { data } = await api.get("/admin/planification/access");
  return data;
}
export async function adminPatchSettings(patch) {
  const { data } = await api.patch("/admin/settings", patch);
  return data;
}
export async function adminForceLogoutAllAgencies() {
  const { data } = await api.post("/admin/settings/force-logout-all-agencies");
  return data;
}
export async function adminForceLogoutAllAdmins() {
  const { data } = await api.post("/admin/settings/force-logout-all-admins");
  return data;
}
export async function adminForceLogoutAgency(agencyId) {
  const { data } = await api.post(`/admin/agencies/${encodeURIComponent(agencyId)}/force-logout`);
  return data;
}

// ====================================================================
// Iter58 (Phase 5) — Command-Center landing data
// ====================================================================
export async function adminCommandCenterSummary(params = {}) {
  const { data } = await api.get("/admin/command-center/summary", { params });
  return data;
}
export async function adminCommandCenterActiveAlerts(params = {}) {
  const { data } = await api.get("/admin/command-center/active-alerts", { params });
  return data;
}

// ====================================================================
// Iter57 (Phase 4) — Audit log feed + CSV export
// ====================================================================
export async function adminListAuditLogs(params = {}) {
  const { data } = await api.get("/admin/audit-logs", { params });
  return data;
}
export async function adminAuditLogsMeta() {
  const { data } = await api.get("/admin/audit-logs/meta");
  return data;
}
export async function adminAuditLogsCsvBlob(params = {}) {
  const res = await api.get("/admin/audit-logs.csv", {
    params,
    responseType: "blob",
  });
  return res.data;
}

// Agency auth + dispatch
export async function agencyLogin(email, password) {
  // Backend sets rr_agency_token + rr_agency_csrf cookies on success.
  const { data } = await api.post("/agency/login", { email, password });
  return data; // { token (legacy), csrf_token (legacy), agency }
}
export async function agencyLogout() {
  try { await api.post("/agency/logout", {}); } catch (e) { /* noop */ void e; }
}
export async function agencyMe() {
  const { data } = await api.get("/agency/me");
  return data;
}
export async function agencyListAlerts(params = {}) {
  const { data } = await api.get("/agency/alerts", { params });
  return data;
}
export async function agencyUpdateIntervention(alertId, payload) {
  const { data } = await api.patch(`/agency/alerts/${alertId}/intervention`, payload);
  return data;
}


// ─── Safety heatmap (public, aggregated) ───────────────────────────────
export async function fetchSafetyHeatmap(sinceHours = 168) {
  const { data } = await api.get("/map/safety-heatmap", { params: { since_hours: sinceHours } });
  return data;
}

// ─── Family Live View ──────────────────────────────────────────────────
export async function createLiveShare({ label, durationHours }) {
  const ownerPhone = userIdentity.getPhone();
  const { data } = await api.post("/live-shares", {
    owner_phone: ownerPhone,
    label: label || null,
    duration_hours: durationHours || 3,
  });
  return data;
}
export async function pushLiveSharePosition(token, { latitude, longitude, accuracy, heading, speed }) {
  const { data } = await api.patch(`/live-shares/${token}/position`, {
    latitude, longitude, accuracy,
    heading: heading ?? null,
    speed: speed ?? null,
  });
  return data;
}
export async function getLiveShare(token) {
  const { data } = await api.get(`/live-shares/${token}`);
  return data;
}
export async function stopLiveShare(token) {
  const { data } = await api.post(`/live-shares/${token}/stop`, {});
  return data;
}
export async function listMyLiveShares() {
  const phone = userIdentity.getPhone();
  if (!phone) return { items: [] };
  // Security fix (SEC-002): ownership is now verified server-side via
  // the X-Citizen-Token header (attached automatically by the axios
  // interceptor above) — the backend no longer trusts a raw `?phone=`.
  const { data } = await api.get("/live-shares/me/by-phone");
  return data;
}


// ─── System / sovereignty introspection (read-only) ────────────────────
export async function fetchDeploymentMode() {
  const { data } = await api.get("/system/deployment-mode");
  return data;
}
